import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import authService from '../services/auth';
import { useAuth } from '../context/AuthContext';

/**
 * Opened from the link mailed to a NEW address after a signed-in user asked to
 * change theirs (POST /auth/change-email). Public, because the link is often
 * opened on another device: the token is the proof.
 */
const ConfirmEmailChange = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, refreshUser } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('This link is incomplete. Open it straight from the email, or ask for a new one.');
      return;
    }

    authService.confirmEmailChange(token)
      .then((email) => {
        setStatus('success');
        setMessage(`Your email address is now ${email}. Use it next time you sign in.`);
        // Show the new address in this session too. A failed refresh would sign
        // the user out, so only try when signed in and ignore any failure.
        if (isAuthenticated) refreshUser().catch(() => {});
      })
      .catch((err: unknown) => {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Could not change your email address.');
      });
  }, [searchParams, isAuthenticated, refreshUser]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-extrabold text-gray-900">New email address</h1>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="flex flex-col items-center space-y-4 text-center">
            {status === 'loading' && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                <p className="text-gray-600">Confirming your new address…</p>
              </>
            )}

            {status === 'success' && (
              <>
                <p className="text-green-700 font-medium break-words">{message}</p>
                <Link to={isAuthenticated ? '/settings/security' : '/login'}>
                  <Button>{isAuthenticated ? 'Back to Account & Security' : 'Sign in'}</Button>
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <p className="text-error-700 break-words">{message}</p>
                <Link to={isAuthenticated ? '/settings/security' : '/login'}>
                  <Button variant="outline">{isAuthenticated ? 'Account & Security' : 'Sign in'}</Button>
                </Link>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ConfirmEmailChange;
